import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Form } from '@/components/ui/form';
import InformationFields from '../common/InformationFields';
import AddressFields from '../common/AddressFields';
import RegionFields from '../common/RegionFields';
import { formSchema } from '../common/schema';

import { useQuery } from '@/helpers/useQuery';
import SpinnerButton from '@/components/common/SpinnerButton';
import Error from '@/components/common/Error';
import { convertToISODateString } from '@/helpers/dateHelpers';
import { usePutCoachSelf, useCoachSelf } from '@/data/coach';
import Input from '@/components/form/Input';
import WorkExperienceField from '../common/WorkExperienceField';
import { useApplication } from '@/data/application';

const APPLICATION_STEP_KEY = 'personal-information';

const EditCoachForm = ({ coach }) => {
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const putCoach = usePutCoachSelf();
  const coachSelf = useCoachSelf();
  const navigate = useNavigate();
  const activityId = useQuery('activityId');
  const coachActivityId = useQuery('coachActivityId');
  const applicationStructure = useApplication({ coachActivityId });

  const step = applicationStructure.data?.steps.find(
    (step) => step.key === APPLICATION_STEP_KEY
  );

  const coachAddress = coach.addresses[0];

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      phone: coach.phone,
      birthDate: coach.birthDate ? new Date(coach.birthDate) : '',
      hometown: coach.hometown || '',
      address: coachAddress?.address || '',
      city: coachAddress?.city || '',
      stateCode: coachAddress?.stateCode || '',
      zipCode: coachAddress?.zipCode || '',
      regions: coach.coachRegions?.map((cr) => cr.regionId) || [],
      workExperience: coach.workExperience || ''
    }
  });

  const onSubmit = async (values) => {
    try {
      setSubmitting(true);
      setApiError('');
      const address = {
        id: coachAddress.id,
        address: values.address,
        city: values.city,
        stateCode: values.stateCode,
        zipCode: values.zipCode,
        countryCode: 'CA'
      };
      const payload = {
        phone: values.phone,
        birthDate: convertToISODateString(values.birthDate),
        hometown: values.hometown,
        address,
        regions: values.regions,
        workExperience: values.workExperience,
        activityId
      };

      const updatedCoach = await putCoach(payload);
      coachSelf.mutate(updatedCoach, false);
      if (step?.nextStepLink) {
        navigate(step.nextStepLink);
      } else {
        const coachActivity = updatedCoach.coachActivities.find(
          (ca) => ca.activityId === activityId
        );
        navigate(
          `/application/identification/${updatedCoach.id}?coachActivityId=${coachActivity.id}`
        );
      }
    } catch (err) {
      setApiError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <InformationFields />
        <AddressFields />
        <RegionFields />
        <WorkExperienceField />
        <Input name="hometown" label="Hometown (optional)" />
        {apiError && <Error error={apiError} />}
        <div className="flex justify-end">
          <SpinnerButton loading={submitting}>Continue</SpinnerButton>
        </div>
      </form>
    </Form>
  );
};

export default EditCoachForm;
